"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Mission() {
  const {
    ref: ruleRef,
    isVisible: ruleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.15
  });
  const {
    ref: blocksRef,
    isVisible: blocksVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const pillars = [{
    title: "Unbiased Truth",
    body: "We strip away the noise to deliver facts. Our editorial process prioritizes objective reporting, ensuring that every story is verified, balanced, and free from external influence."
  }, {
    title: "Amplifying Voices",
    body: "We actively seek out perspectives that are frequently overlooked. By centering underrepresented communities, we provide a more complete and accurate picture of our shared reality."
  }, {
    title: "Community Trust",
    body: "Transparency is our foundation. We invite our readers to engage with our process, challenge our findings, and participate in building a news platform that serves the public interest."
  }];
  return <section data-section-id="10812" id="our-editorial-mission" className="bg-background text-foreground py-32 md:py-44 font-default">
      <div className="mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div className="grid grid-cols-12 gap-4 md:gap-6 items-start">
          <div ref={ruleRef} className="col-span-12 relative h-px bg-foreground origin-left transition-transform duration-[520ms] ease-[cubic-bezier(0.7,0,0.3,1)]" style={{
          transform: ruleVisible ? "scaleX(1)" : "scaleX(0)"
        }} aria-hidden="true" />
          <div className="col-span-12 mt-4">
            <span className="text-[0.8125rem] font-medium uppercase tracking-[0.04em] text-foreground">
              MISSION
            </span>
          </div>
        </div>

        {}
        <div className="grid grid-cols-12 gap-4 md:gap-6 mt-16 md:mt-24">
          <div ref={eyebrowRef} className="col-span-12 md:col-start-2 md:col-span-10 transition-all duration-[420ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]" style={{
          opacity: eyebrowVisible ? 1 : 0,
          transform: eyebrowVisible ? "translateY(0)" : "translateY(12px)"
        }}>
            <h4 className="text-[1.125rem] md:text-[1.375rem] font-default font-medium text-muted-foreground tracking-[-0.005em] leading-[1.3]">
              Our commitment to the public
            </h4>
          </div>
        </div>

        {}
        <div className="grid grid-cols-12 gap-4 md:gap-6 mt-8 md:mt-10">
          <h2 ref={headlineRef} className="col-span-12 md:col-start-2 md:col-span-10 text-foreground font-default font-semibold leading-[0.98]" style={{
          fontSize: "clamp(2.75rem, 5.2vw, 5.5rem)",
          letterSpacing: "-0.025em"
        }}>
            <span className="inline-block transition-all duration-[420ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]" style={{
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(12px)",
            transitionDelay: "0ms"
          }}>
              {"Diversity is our strength \u2014 "}
            </span>
            <span className="inline-block italic transition-all duration-[420ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]" style={{
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(12px)",
            color: headlineVisible ? "var(--chart-1)" : "var(--foreground)",
            transitionDelay: "120ms, 120ms, 320ms",
            transitionProperty: "opacity, transform, color"
          }}>
              inclusion
            </span>
            <span className="inline-block transition-all duration-[420ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]" style={{
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(12px)",
            transitionDelay: "240ms"
          }}>
              {" is the mission that drives our reporting."}
            </span>
          </h2>
        </div>

        {}
        <div className="grid grid-cols-12 gap-4 md:gap-6 mt-16 md:mt-20">
          <div className="col-span-12 md:col-start-2 md:col-span-10 h-px bg-foreground" aria-hidden="true" />
        </div>

        {}
        <div ref={blocksRef} className="grid grid-cols-12 gap-4 md:gap-6 mt-12 md:mt-16">
          {pillars.map((pillar, i) => <div data-index={i} key={pillar.title} className="col-span-12 md:col-start-2 md:col-span-10 md:col-auto transition-all duration-[420ms] ease-[cubic-bezier(0.2,0.7,0.2,1)] border-t border-border md:border-t-0 pt-8 md:pt-0 first:border-t-0 first:pt-0" style={{
          opacity: blocksVisible ? 1 : 0,
          transform: blocksVisible ? "translateY(0)" : "translateY(12px)",
          transitionDelay: `${i * 60}ms`,
          gridColumn: undefined
        }}>
              <div className="md:hidden">
                <h4 className="text-[1.125rem] font-default font-medium tracking-[-0.005em] leading-[1.3] text-foreground mb-3">
                  {pillar.title}
                </h4>
                <p className="text-[0.9375rem] font-default leading-[1.6] text-muted-foreground">
                  {pillar.body}
                </p>
              </div>
            </div>)}

          {}
          <div className="hidden md:contents">
            {pillars.map((pillar, i) => {
            const colStart = 2 + i * 3 + (i > 0 ? 1 : 0);
            return <div data-index={i} key={`desk-${pillar.title}`} className="transition-all duration-[420ms] ease-[cubic-bezier(0.2,0.7,0.2,1)]" style={{
              opacity: blocksVisible ? 1 : 0,
              transform: blocksVisible ? "translateY(0)" : "translateY(12px)",
              transitionDelay: `${i * 60}ms`,
              gridColumn: `${colStart} / span 3`
            }}>
                  <h4 className="text-[1.125rem] lg:text-[1.375rem] font-default font-medium tracking-[-0.005em] leading-[1.3] text-foreground mb-4">
                    {pillar.title}
                  </h4>
                  <p className="text-[0.9375rem] font-default leading-[1.6] text-muted-foreground">
                    {pillar.body}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}